import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { copyText } from "@/utils/copyText";

import { formatWebhookMoment } from "../../utils/describeWebhookSigning";
import { WebhookSignatureGuidance } from "./WebhookSignatureGuidance";

export type WebhookSecretDialogVariant = "created" | "rotated" | "revealed";

export interface WebhookSecretDialogProps {
	variant: WebhookSecretDialogVariant;
	webhookId: number;
	webhookName: string;
	secret: string;
	/** After a rotation: until when the replaced secret still signs. */
	previousSecretValidUntil?: string | null;
	onClose: () => void;
}

const TITLE_KEY: Record<WebhookSecretDialogVariant, string> = {
	created: "webhookSigning.secret.createdTitle",
	rotated: "webhookSigning.secret.rotatedTitle",
	revealed: "webhookSigning.secret.revealedTitle",
};

const LEAD_KEY: Record<WebhookSecretDialogVariant, string> = {
	created: "webhookSigning.secret.createdLead",
	rotated: "webhookSigning.secret.rotatedLead",
	revealed: "webhookSigning.secret.revealedLead",
};

/**
 * A signing secret, shown this one time (PREC-4249): after create, after a rotation, or by the
 * one-time reveal of a webhook created before signing. Escape and the backdrop do nothing: the
 * secret cannot be fetched again, so only the explicit "I have copied the secret" closes it.
 */
export const WebhookSecretDialog: FunctionComponent<WebhookSecretDialogProps> = ({
	variant,
	webhookId,
	webhookName,
	secret,
	previousSecretValidUntil,
	onClose,
}) => {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		setCopied(await copyText(secret));
	};

	return (
		<Modal
			footer={
				<div className="flex justify-end">
					<Button
						color={ButtonColor.ACTION}
						size={ButtonSize.S}
						testId="webhook-secret-done"
						title={t("webhookSigning.secret.done")}
						type="button"
						onClick={onClose}
					/>
				</div>
			}
			isOpened={true}
			size="md"
			title={t(TITLE_KEY[variant], { name: webhookName })}
		>
			<div className="flex flex-col gap-3 text-sm text-text" data-test="webhook-secret-dialog">
				<p>{t(LEAD_KEY[variant])}</p>
				<div>
					<div className="mb-1 text-xs font-medium text-text-meta">
						{t(
							variant === "rotated"
								? "webhookSigning.secret.newLabel"
								: "webhookSigning.secret.label"
						)}
					</div>
					<div className="flex items-center gap-2">
						<code
							className="grow break-all rounded-md border border-border-light bg-background-light px-2 py-1.5 font-mono text-[13px]"
							data-test="webhook-secret-value"
						>
							{secret}
						</code>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.XS}
							testId="webhook-secret-copy"
							title={t(copied ? "webhookSigning.secret.copied" : "webhookSigning.secret.copy")}
							type="button"
							onClick={handleCopy}
						/>
					</div>
				</div>
				{variant === "rotated" && previousSecretValidUntil && (
					<p className="text-xs">
						{t("webhookSigning.secret.rotatedOverlap", {
							date: formatWebhookMoment(previousSecretValidUntil),
						})}
					</p>
				)}
				{variant === "revealed" && (
					<p className="text-xs">{t("webhookSigning.secret.revealedNote")}</p>
				)}
				{variant === "created" && <WebhookSignatureHeaders webhookId={webhookId} />}
				{variant !== "rotated" && <WebhookSignatureGuidance variant="full" />}
			</div>
		</Modal>
	);
};

/** The headers every delivery carries, with this webhook's id (frame 2 of the mockup). */
const WebhookSignatureHeaders: FunctionComponent<{ webhookId: number }> = ({ webhookId }) => {
	const { t } = useTranslation();

	return (
		<div className="text-xs" data-test="webhook-signature-headers">
			<div className="mb-1 font-medium">{t("webhookSigning.headers.title")}</div>
			<pre className="whitespace-pre-wrap break-all rounded-md bg-background-light p-2 font-mono text-[12px]">
				{[
					"X-Signalist-Signature: t=<unix seconds>,v1=<hex HMAC-SHA256>",
					`X-Signalist-Webhook-Id: ${webhookId}`,
					"X-Signalist-Delivery-Id: <uuid>",
					"Content-Type: application/json",
				].join("\n")}
			</pre>
			<div className="mt-1 text-text-meta">{t("webhookSigning.headers.formula")}</div>
		</div>
	);
};
